import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Autocomplete, TextField } from '@mui/material';
import dayjs from 'dayjs';
import { useNavigate, useParams } from 'react-router-dom';
import axiosInstant from '../../../api';
import { salesProposalDetailEndpoint, videoShortlistsEndpoint } from '../../../api/endpoints';
import { MainContainer } from '../../../components/ContentWrapper';
import { ProposalFlowModal } from '../../../components/ProposalFlow/ProposalFlowModal';
import { ConvertProposalModal } from './ConvertProposalModal';
import { ProposalStrategyView } from '../../../components/ProposalFlow/strategy/ProposalStrategyView';
import { strategyFromProposal } from '../../../components/ProposalFlow/strategy/StrategyData';
import {
    downloadProposalPdf,
    pdfInputFromDetail,
} from '../../../components/ProposalFlow/pdf/downloadProposalPdf';
import { useProposalDetailPreview } from '../../../components/ProposalFlow/useProposalDetailPreview';
import { useProposalExamplesWithRefetch } from '../../../components/ProposalFlow/useProposalExamples';
import { ProposalDetail, ProposalExamplesResponse } from '../../../components/ProposalFlow/types';
import { ExampleCoverageGrid } from './ExampleCoverageGrid';
import { DotLottleLoader } from '../../../components/Loader';
import { formatCurrency } from '../../../utils/formatCurrency';
import { useUser } from '../../../hooks/useUser';
import { hideAllPricing } from '../../../utils';

// Internal proposal detail: full slide-3 strategy layout with pricing
// breakdowns, Download PDF, Copy share link, and Edit (reopens the wizard
// prefilled; PATCHes in place). Confirmed proposals are read-only.
const chip = (label: string, tone: 'gold' | 'grey' | 'green' = 'grey') => {
    const tones: Record<string, string> = {
        gold: 'bg-tint text-accent-text',
        grey: 'bg-surface-2 text-text-2',
        green: 'bg-up-bg text-up',
    };
    return (
        <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full whitespace-nowrap ${tones[tone]}`}>
            {label}
        </span>
    );
};

export default function ProposalDetailPage() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [detail, setDetail] = useState<ProposalDetail | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [editOpen, setEditOpen] = useState(false);
    const [convertOpen, setConvertOpen] = useState(false);
    const [copied, setCopied] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const { user, role } = useUser();
    // RMN pricing hide (TDC-1591): a proposal on an rmn:true brand (or
    // stamped under a network) shows no pricing here either — internal
    // staff included.
    const pricingHidden = hideAllPricing(role, user, detail);
    const preview = useProposalDetailPreview(detail);
    const { examples, refetch: refetchExamples } = useProposalExamplesWithRefetch(
        detail && detail.product_category ? detail.product_category.id : null,
        detail ? detail.content_type_units.map((u) => u.content_type_id) : [],
        !!detail,
        detail ? detail.channels.map((ch) => ch.id) : [],
    );

    // Attached-shortlist videos ride along as extra examples everywhere the
    // strip renders (web + PDF).
    const mergedExamples = useMemo<ProposalExamplesResponse | null>(() => {
        const shortlistVideos = detail?.video_shortlist?.videos || [];
        if (!examples && shortlistVideos.length === 0) return examples;
        const videos = [...(examples?.videos || [])];
        shortlistVideos.forEach((v) => {
            if (!videos.some((x) => x.id === v.id)) videos.push(v);
        });
        return {
            product_category_id: examples?.product_category_id ?? 0,
            videos,
            slots: examples?.slots,
            experts: examples?.experts || [],
        };
    }, [examples, detail]);

    // Shortlist attach UI state.
    const [shortlistOptions, setShortlistOptions] = useState<Array<{ id: number; name: string }>>([]);
    const [savingShortlist, setSavingShortlist] = useState(false);
    useEffect(() => {
        (async () => {
            try {
                const { data } = await axiosInstant.get(`${videoShortlistsEndpoint}?all=true`);
                setShortlistOptions(data.rows || []);
            } catch {
                setShortlistOptions([]);
            }
        })();
    }, []);

    const setShortlist = async (shortlistId: number | null) => {
        if (!detail) return;
        setSavingShortlist(true);
        try {
            const { data } = await axiosInstant.patch<ProposalDetail>(
                salesProposalDetailEndpoint(detail.id),
                { video_shortlist_id: shortlistId },
            );
            setDetail(data);
        } finally {
            setSavingShortlist(false);
        }
    };

    const load = useCallback(async () => {
        if (!id) return;
        setLoading(true);
        setError(null);
        try {
            const { data } = await axiosInstant.get<ProposalDetail>(salesProposalDetailEndpoint(id));
            setDetail(data);
        } catch (err: any) {
            const status = err && err.response ? err.response.status : null;
            setError(status === 404 ? "Strategy not found." : "Failed to load the strategy.");
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        load();
    }, [load]);

    const shareUrl = detail && detail.share_token
        ? `${window.location.origin}/proposal/view/${detail.share_token}`
        : null;

    const copyShareLink = async () => {
        if (!shareUrl) return;
        try {
            await navigator.clipboard.writeText(shareUrl);
            setCopied(true);
            setTimeout(() => setCopied(false), 1800);
        } catch {
            // Clipboard unavailable — the URL is still visible in the PDF.
        }
    };

    const download = async () => {
        if (!detail || downloading) return;
        setDownloading(true);
        try {
            await downloadProposalPdf(
                pdfInputFromDetail(detail, {
                    internal: true,
                    showPricing: !pricingHidden,
                    showPriceTeaser: !pricingHidden,
                    preview,
                    shareUrl,
                    examples: mergedExamples,
                }),
            );
        } finally {
            setDownloading(false);
        }
    };

    if (loading) {
        return (
            <MainContainer>
                <div className="flex justify-center py-16">
                    <DotLottleLoader fill={false} />
                </div>
            </MainContainer>
        );
    }

    if (error || !detail) {
        return (
            <MainContainer>
                <div className="py-16 text-center text-text-2">
                    <div className="text-[15px] font-semibold mb-2">{error || "Strategy not found."}</div>
                </div>
            </MainContainer>
        );
    }

    const editable = detail.status === "proposal";

    return (
        <MainContainer>
            <div className="mb-5">
                <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                        <h1 className="text-[20px] font-bold text-text-1 truncate">{detail.name}</h1>
                        <div className="flex flex-wrap items-center gap-2 mt-2">
                            {chip(detail.status === 'proposal' ? 'Strategy' : detail.status, editable ? 'gold' : 'green')}
                            {detail.tier && chip(`${detail.tier.charAt(0).toUpperCase()}${detail.tier.slice(1)} package`, 'grey')}
                            {detail.brand && chip(detail.brand.name, 'grey')}
                            {!detail.brand && detail.proposal_meta && detail.proposal_meta.brand_name
                                ? chip(`${detail.proposal_meta.brand_name} (unverified)`, 'grey')
                                : null}
                            {detail.lead && chip(detail.lead.email, 'grey')}
                            {chip(dayjs(detail.created_at).format('MMM DD, YYYY'), 'grey')}
                            {detail.pricing.total != null && !pricingHidden && chip(formatCurrency(detail.pricing.total), 'gold')}
                            {detail.pdp_audit_report && (
                                <a
                                    href={detail.pdp_audit_report.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    title={detail.pdp_audit_report.url}
                                    className="text-[11px] font-semibold px-2.5 py-1 rounded-full whitespace-nowrap bg-[#EEF3FA] text-[#3B6EA0] no-underline"
                                >
                                    🔍 From PDP audit ·{" "}
                                    {dayjs(detail.pdp_audit_report.created_at).format("MMM DD")}
                                </a>
                            )}
                        </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        <button
                            type="button"
                            onClick={download}
                            disabled={downloading}
                            className="text-[13px] font-semibold text-white bg-[#1C1A17] rounded-[10px] px-4 py-2.5"
                        >
                            {downloading ? "Preparing…" : "Download PDF ↓"}
                        </button>
                        {shareUrl && (
                            <button
                                type="button"
                                onClick={copyShareLink}
                                className="text-[13px] font-semibold text-accent-text border border-accent rounded-[10px] px-4 py-2.5 bg-transparent"
                            >
                                {copied ? "✓ Link copied" : "Copy share link"}
                            </button>
                        )}
                        {editable && (
                            <button
                                type="button"
                                onClick={() => setEditOpen(true)}
                                className="text-[13px] font-semibold text-text-2 border border-border-token rounded-[10px] px-4 py-2.5 bg-transparent"
                            >
                                Edit proposal
                            </button>
                        )}
                        {editable && (
                            <button
                                type="button"
                                onClick={() => setConvertOpen(true)}
                                className="text-[13px] font-semibold text-white bg-accent rounded-[10px] px-4 py-2.5 border-0"
                            >
                                Convert to order →
                            </button>
                        )}
                    </div>
                </div>
                {!editable && (
                    <div className="mt-3 text-[12.5px] text-text-2 bg-surface-2 border border-border-token rounded-[10px] px-4 py-2.5">
                        This proposal has been confirmed into an order and is read-only. The share link and PDF
                        remain available.
                    </div>
                )}
            </div>

            <ProposalStrategyView
                data={strategyFromProposal(detail)}
                showPricingBreakdowns={!pricingHidden}
                perContentTypeEstimates={
                    detail.tier && preview && preview.tiers[detail.tier]
                        ? preview.tiers[detail.tier]!.per_content_type_estimates || null
                        : null
                }
                examples={mergedExamples}
            />

            {examples && examples.slots && (
                <ExampleCoverageGrid
                    detail={detail}
                    slots={examples.slots}
                    onChanged={refetchExamples}
                />
            )}

            <div className="mt-6 flex flex-wrap items-center gap-3 border border-border-token rounded-lg bg-surface px-4 py-3">
                <div className="min-w-0">
                    <div className="text-[13.5px] font-bold text-text-1">Example shortlist</div>
                    <div className="text-[12px] text-text-2">
                        Attach a curated shortlist for additional example videos on this proposal.
                    </div>
                </div>
                <div className="flex items-center gap-2 ml-auto">
                    {detail.video_shortlist ? (
                        <>
                            <span className="text-[12px] font-semibold text-accent-text bg-tint px-2.5 py-1 rounded-full">
                                🎬 {detail.video_shortlist.name} · {detail.video_shortlist.videos.length}{" "}
                                videos
                            </span>
                            <button
                                type="button"
                                disabled={savingShortlist}
                                onClick={() => setShortlist(null)}
                                className="text-[12px] font-semibold text-text-2 bg-transparent border border-border-token rounded-md px-2.5 py-1 cursor-pointer"
                            >
                                Detach
                            </button>
                        </>
                    ) : (
                        <Autocomplete
                            size="small"
                            sx={{ width: 280 }}
                            options={shortlistOptions}
                            getOptionLabel={(o) => o.name}
                            disabled={savingShortlist}
                            onChange={(_, value) => value && setShortlist(value.id)}
                            renderInput={(params) => (
                                <TextField {...params} placeholder="Attach a shortlist…" />
                            )}
                        />
                    )}
                </div>
            </div>

            {editable && (
                <ProposalFlowModal
                    open={editOpen}
                    onClose={() => setEditOpen(false)}
                    edit={{ proposal: detail, endpoint: salesProposalDetailEndpoint(detail.id) }}
                    onSaved={(updated) => {
                        setDetail(updated);
                        setEditOpen(false);
                    }}
                    onCancelEdit={() => setEditOpen(false)}
                />
            )}
            {editable && (
                <ConvertProposalModal
                    proposal={detail}
                    open={convertOpen}
                    onClose={() => setConvertOpen(false)}
                    onConverted={(orderId) => {
                        setConvertOpen(false);
                        navigate(`/video-production/projects?orderId=${orderId}`);
                    }}
                />
            )}
        </MainContainer>
    );
}
